import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { getProduct, getProducts } from '@services/product.service'
import { useCartStore } from '@store/useCartStore'
import { useToastStore } from '@store/useToastStore'
import Spinner from '@components/UI/Spinner'
import './ProductDetailPage.scss'

const BG_GRADIENTS = [
  'linear-gradient(135deg, var(--tan) 0%, var(--cream) 100%)',
  'linear-gradient(135deg, #EAD9C8, #F8F3ED)',
  'linear-gradient(135deg, #E8E0D8, #F5EEE6)',
]

function SachetVisual({ large }) {
  const cls = large ? 'pdp-sachet large' : 'pdp-sachet'
  return (
    <div className={cls}>
      <div className="sachet-ribbon" />
      <div className="petal" />
      <div className="petal" />
      <div className="petal" />
      <div className="mini-leaf" />
      <div className="mini-leaf" />
    </div>
  )
}

function HexVisual({ large }) {
  const cls = large ? 'pdp-hex large' : 'pdp-hex'
  return (
    <div className={cls}>
      <div className="petal" />
      <div className="petal" />
    </div>
  )
}

function BurnerVisual({ large }) {
  const cls = large ? 'pdp-burner large' : 'pdp-burner'
  return (
    <div className={cls}>
      <div className="burner-top" />
      <div className="melt-chunk c1" />
      <div className="melt-chunk c2" />
      <div className="melt-chunk c3" />
      <div className="burner-arch" />
    </div>
  )
}

const VISUALS = [SachetVisual, HexVisual, BurnerVisual]

function ProductGallery({ product, mainIndex }) {
  const [activeImg, setActiveImg] = useState(0)
  const images = product.images ?? []
  const hasPhotos = images.length > 0
  const hasMainImage = product.main_image && product.main_image.trim()
  const Visual = VISUALS[mainIndex % VISUALS.length]
  const gradient = BG_GRADIENTS[mainIndex % BG_GRADIENTS.length]

  if (!hasPhotos && !hasMainImage) {
    return (
      <div className="pdp-gallery">
        <div className="pdp-gallery-main" style={{ background: gradient }}>
          <Visual large />
        </div>
      </div>
    )
  }

  const allImages = hasPhotos ? images : [product.main_image]

  return (
    <div className="pdp-gallery">
      <div className="pdp-gallery-main">
        <img
          src={allImages[activeImg] || allImages[0]}
          alt={product.name}
          className="pdp-gallery-img"
        />
      </div>
      {allImages.length > 1 && (
        <div className="pdp-gallery-thumbs">
          {allImages.map((src, i) => (
            <button
              key={i}
              type="button"
              className={`pdp-thumb ${i === activeImg ? 'active' : ''}`}
              onClick={() => setActiveImg(i)}
              aria-label={`Imagine ${i + 1}`}
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function VariantSelector({ variants, selected, onSelect }) {
  if (!variants || variants.length === 0) return null
  return (
    <div className="pdp-variants">
      <span className="pdp-variants-label">Aromă</span>
      <div className="pdp-variants-list">
        {variants.map((v) => {
          const outOfStock = v.stock != null && v.stock <= 0
          return (
            <button
              key={v.name}
              type="button"
              className={`pdp-variant ${selected === v.name ? 'active' : ''} ${outOfStock ? 'out' : ''}`}
              onClick={() => !outOfStock && onSelect(v.name)}
              disabled={outOfStock}
              aria-label={v.name}
            >
              {v.name}
              {outOfStock && <span className="pdp-variant-oos">Indisponibil</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function QuantityPicker({ qty, setQty, max = 99 }) {
  return (
    <div className="pdp-qty">
      <button
        type="button"
        className="pdp-qty-btn"
        onClick={() => setQty(Math.max(1, qty - 1))}
        aria-label="Scade cantitatea"
      >
        −
      </button>
      <span className="pdp-qty-value">{qty}</span>
      <button
        type="button"
        className="pdp-qty-btn"
        onClick={() => setQty(Math.min(max, qty + 1))}
        aria-label="Crește cantitatea"
      >
        +
      </button>
    </div>
  )
}

function RelatedCard({ product, index }) {
  const Visual = VISUALS[index % VISUALS.length]
  const gradient = BG_GRADIENTS[index % BG_GRADIENTS.length]
  const hasImage = product.main_image && product.main_image.trim()
  return (
    <Link to={`/product/${product.slug}`} className="pdp-related-card">
      <div className="pdp-related-img" style={{ background: gradient }}>
        {hasImage ? (
          <img src={product.main_image} alt={product.name} className="pdp-related-photo" />
        ) : (
          <div className="pdp-related-visual"><Visual /></div>
        )}
      </div>
      <div className="pdp-related-name">{product.name}</div>
      <div className="pdp-related-price">{product.price} lei</div>
    </Link>
  )
}

export default function ProductDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const addItem = useCartStore((s) => s.addItem)
  const addToast = useToastStore((s) => s.addToast)

  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [related, setRelated] = useState([])

  const variants = useMemo(() => {
    if (!product?.variants) return []
    const parsed = typeof product.variants === 'string'
      ? JSON.parse(product.variants)
      : product.variants
    return Array.isArray(parsed) ? parsed : []
  }, [product])

  const tags = useMemo(() => {
    if (!product?.tags) return []
    const parsed = typeof product.tags === 'string' ? JSON.parse(product.tags) : product.tags
    return Array.isArray(parsed) ? parsed : []
  }, [product])

  const [selectedVariant, setSelectedVariant] = useState(null)
  const [qty, setQty] = useState(1)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setProduct(null)
    setQty(1)
    setSelectedVariant(null)

    getProduct(slug)
      .then((res) => {
        if (cancelled) return
        const p = res?.data
        if (!p) return navigate('/shop', { replace: true })
        setProduct(p)
        const v = typeof p.variants === 'string' ? JSON.parse(p.variants) : (p.variants ?? [])
        if (Array.isArray(v) && v.length > 0) {
          const first = v.find((vi) => vi.stock == null || vi.stock > 0)
          setSelectedVariant(first?.name ?? v[0].name)
        }
      })
      .catch(() => {
        if (!cancelled) navigate('/shop', { replace: true })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [slug, navigate])

  useEffect(() => {
    if (!product?.category_id) return
    let cancelled = false
    getProducts({ categoryId: product.category_id, pageSize: 4 })
      .then((res) => {
        if (cancelled) return
        const items = (res?.data?.items ?? []).filter((p) => p.id !== product.id)
        setRelated(items.slice(0, 3))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [product])

  const handleAddToCart = () => {
    if (variants.length > 0 && !selectedVariant) {
      addToast({ message: 'Te rugăm selectează o aromă', type: 'warning' })
      return
    }
    addItem(product, selectedVariant, qty)
    addToast({ message: `${product.name} adăugat în coș`, type: 'success' })
  }

  if (loading) {
    return (
      <div className="pdp-page pdp-loading">
        <Spinner />
      </div>
    )
  }

  if (!product) return null

  const descriptionParagraphs = (product.description || '').split('\n').filter(Boolean)
  const hasCompare = product.compare_price != null && Number(product.compare_price) > Number(product.price)
  const inStock = product.stock_quantity == null || product.stock_quantity > 0
  const visualIndex = slug ? slug.charCodeAt(0) % 3 : 0

  return (
    <div className="pdp-page">
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Acasă</Link>
        <span className="pdp-breadcrumb-sep">/</span>
        <Link to="/shop">Magazin</Link>
        <span className="pdp-breadcrumb-sep">/</span>
        <span>{product.name}</span>
      </nav>

      <div className="pdp-content">
        <ProductGallery product={product} mainIndex={visualIndex} />

        <div className="pdp-info">
          {product.short_description && (
            <div className="pdp-eyebrow">{product.short_description}</div>
          )}

          <h1 className="pdp-name">{product.name}</h1>

          <div className="pdp-price-row">
            {hasCompare && (
              <span className="pdp-compare">{product.compare_price} lei</span>
            )}
            <span className="pdp-price">{product.price} lei</span>
          </div>

          <div className="pdp-description">
            {descriptionParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <VariantSelector
            variants={variants}
            selected={selectedVariant}
            onSelect={setSelectedVariant}
          />

          <div className="pdp-actions">
            <QuantityPicker qty={qty} setQty={setQty} max={product.stock_quantity || 99} />
            <button
              type="button"
              className="pdp-add-btn"
              onClick={handleAddToCart}
              disabled={!inStock}
            >
              {inStock ? 'Adaugă în coș' : 'Stoc epuizat'}
            </button>
          </div>

          {!inStock && (
            <p className="pdp-oos">Momentan nu este disponibil.</p>
          )}

          {tags.length > 0 && (
            <div className="pdp-tags">
              {tags.map((t) => (
                <span key={t} className="pdp-tag">#{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="pdp-related">
          <div className="section-label">Produse similare</div>
          <h2 className="pdp-related-title">
            S-ar putea să-ți <em>placă</em>
          </h2>
          <div className="pdp-related-grid">
            {related.map((p, i) => (
              <RelatedCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
